import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { api, apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { ListShell } from '../components/ListShell';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Button } from '../components/ui/Button';
import { Pagination } from '../components/people/Pagination';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import {
  BatchDrawer,
  type BatchDrawerMode,
} from '../components/academics/BatchDrawer';
import {
  BatchModeBadge,
  BatchStatusBadge,
} from '../components/academics/StatusBadge';
import {
  type BatchRecord,
  type CourseSummary,
} from '../components/academics/types';
import { formatDate } from '../lib/format';
import type { TeacherRecord, ManagerRecord } from '../components/people/types';
import {
  FilterBar,
  SortableHeader,
  useListFilters,
  type FilterRoute,
} from '../components/filters';
import {
  batchesFilterConfig,
  batchesSearchSchema,
  BATCHES_SORT_COLUMNS,
} from '../components/academics/batchesFilterConfig';

const PAGE_SIZE = 20;

export const Route = createFileRoute('/_authed/academics/batches')({
  validateSearch: batchesSearchSchema,
  component: BatchesPage,
});

function BatchesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const canManage =
    role === 'super_admin' || role === 'academic_admin' || role === 'nalim';

  const [drawer, setDrawer] = useState<BatchDrawerMode | null>(null);

  // The single source of truth for search / filters / sort / page, backed by
  // the route's URL search params. The adapter narrows the route's precisely-
  // typed search/navigate surface to the toolkit's structural `FilterRoute`
  // contract; the route's own Zod `validateSearch` keeps the boundary typed.
  const filterRoute: FilterRoute = {
    useSearch: () => Route.useSearch() as Record<string, unknown>,
    useNavigate: () => {
      const navigate = Route.useNavigate();
      return (opts) =>
        navigate({
          search: ((prev: Record<string, unknown>) => opts.search(prev)) as never,
          ...(opts.replace !== undefined ? { replace: opts.replace } : {}),
        });
    },
  };
  const filters = useListFilters(batchesFilterConfig, filterRoute, {
    pageSize: PAGE_SIZE,
  });
  const { values, apiParams, setSort, setPage } = filters;

  const listQuery = useQuery({
    queryKey: ['batches', 'list', apiParams] as const,
    queryFn: ({ signal }) => api.list<BatchRecord>('/api/batches', apiParams, signal),
    placeholderData: (prev) => prev,
  });

  // The full course + teacher lists power the Course / Teacher COLUMN cells:
  // batch rows carry only `courseId` / `teacherId`, not names, so these maps
  // resolve the display label. This data path is independent of the filter
  // bar's lookup facets (which have their own async loaders). All three queries
  // also feed the BatchDrawer as props.
  const coursesQuery = useQuery({
    queryKey: ['courses', 'list'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: CourseSummary[] }>('/api/courses', { signal }),
    staleTime: 60_000,
  });

  const teachersQuery = useQuery({
    queryKey: ['teachers', 'lookup'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: TeacherRecord[] }>('/api/teachers', {
        signal,
        query: { page: 1, pageSize: 100 },
      }),
    staleTime: 60_000,
  });

  const managersQuery = useQuery({
    queryKey: ['managers', 'lookup'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: ManagerRecord[] }>('/api/managers', {
        signal,
        query: { page: 1, pageSize: 100 },
      }),
    staleTime: 60_000,
  });

  const detailQuery = useQuery({
    queryKey: ['batches', 'drawer-detail', drawer?.kind === 'edit' ? drawer.id : null] as const,
    queryFn: ({ signal }) => {
      if (drawer?.kind !== 'edit') throw new Error('No batch selected');
      return apiRequest<BatchRecord>(`/api/batches/${drawer.id}`, { signal });
    },
    enabled: drawer?.kind === 'edit',
  });

  const courseLookup = useMemo(() => {
    const map = new Map<string, CourseSummary>();
    for (const c of coursesQuery.data?.items ?? []) map.set(c.id, c);
    return map;
  }, [coursesQuery.data]);

  const teacherLookup = useMemo(() => {
    const map = new Map<string, TeacherRecord>();
    for (const t of teachersQuery.data?.items ?? []) map.set(t.id, t);
    return map;
  }, [teachersQuery.data]);

  const columns = useMemo<ColumnDef<BatchRecord, unknown>[]>(() => {
    const base: ColumnDef<BatchRecord, unknown>[] = [
      {
        id: 'name',
        accessorKey: 'name',
        enableSorting: false,
        header: () => (
          <SortableHeader
            label="Name"
            binding={BATCHES_SORT_COLUMNS.name}
            currentSort={values.sort}
            onSort={setSort}
          />
        ),
        cell: (info) => (
          <span className="font-medium text-[color:var(--color-text)]">
            {info.row.original.name}
          </span>
        ),
      },
      {
        id: 'course',
        header: 'Course',
        cell: (info) => {
          const course = courseLookup.get(info.row.original.courseId);
          return (
            <span className="text-[color:var(--color-text-muted)]">
              {course ? course.name : '—'}
            </span>
          );
        },
      },
      {
        id: 'teacher',
        header: 'Teacher',
        cell: (info) => {
          const teacher = teacherLookup.get(info.row.original.teacherId);
          return (
            <span className="text-[color:var(--color-text-muted)]">
              {teacher ? teacher.name : '—'}
            </span>
          );
        },
      },
      {
        id: 'mode',
        header: 'Mode',
        cell: (info) => <BatchModeBadge mode={info.row.original.mode} />,
      },
      {
        id: 'capacity',
        header: 'Capacity',
        cell: (info) => (
          <span className="tabular-nums text-[color:var(--color-text-muted)]">
            {info.row.original.capacity}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (info) => <BatchStatusBadge status={info.row.original.status} />,
      },
      {
        id: 'startDate',
        enableSorting: false,
        header: () => (
          <SortableHeader
            label="Start"
            binding={BATCHES_SORT_COLUMNS.startDate}
            currentSort={values.sort}
            onSort={setSort}
          />
        ),
        cell: (info) => (
          <span className="text-xs text-[color:var(--color-text-muted)]">
            {formatDate(info.row.original.startDate)}
          </span>
        ),
      },
      {
        id: 'endDate',
        header: 'End',
        cell: (info) => (
          <span className="text-xs text-[color:var(--color-text-muted)]">
            {info.row.original.endDate ? formatDate(info.row.original.endDate) : '—'}
          </span>
        ),
      },
    ];

    if (canManage) {
      base.push({
        id: 'edit',
        header: '',
        cell: (info) => (
          <div className="flex items-center justify-end">
            <EditIconButton
              onClick={() => setDrawer({ kind: 'edit', id: info.row.original.id })}
              label={`Edit ${info.row.original.name}`}
              title="Edit batch"
            />
          </div>
        ),
      });
    }

    return base;
  }, [courseLookup, teacherLookup, canManage, values.sort, setSort]);

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const isLoadingList = listQuery.isLoading && !listQuery.data;

  return (
    <ListOrOutlet basePath="/academics/batches">
    <ListShell
      title="Batches"
      description="Cohorts, schedules, and capacity."
      primaryAction={
        <Button
          onClick={() => setDrawer({ kind: 'create' })}
          icon={<Plus size={16} aria-hidden="true" />}
        >
          Add batch
        </Button>
      }
    >
      <FilterBar config={batchesFilterConfig} filters={filters} resultCount={total} />

      {listQuery.isError ? (
        <ErrorState
          title="Could not load batches"
          description={
            listQuery.error instanceof ApiError
              ? listQuery.error.message
              : 'Please try again in a moment.'
          }
          onRetry={() => listQuery.refetch()}
        />
      ) : (
        <>
          <DataTable<BatchRecord>
            data={items}
            columns={columns}
            isLoading={isLoadingList}
            getRowId={(row) => row.id}
            onRowClick={(row) =>
              void navigate({
                to: '/academics/batches/$batchId',
                params: { batchId: row.id },
              })
            }
            emptyState={
              <EmptyState
                title="No batches match these filters"
                description="Try clearing a filter, or create a batch to schedule classes and enroll students."
                action={
                  <Button
                    onClick={() => setDrawer({ kind: 'create' })}
                    icon={<Plus size={16} aria-hidden="true" />}
                  >
                    Add batch
                  </Button>
                }
              />
            }
          />
          <Pagination page={values.page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </>
      )}

      <BatchDrawer
        mode={drawer}
        onClose={() => setDrawer(null)}
        initial={detailQuery.data}
        isLoadingInitial={drawer?.kind === 'edit' && detailQuery.isLoading}
        loadError={detailQuery.error instanceof Error ? detailQuery.error : null}
        courses={coursesQuery.data?.items ?? []}
        teachers={teachersQuery.data?.items ?? []}
        managers={managersQuery.data?.items ?? []}
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: ['batches'] });
          void queryClient.invalidateQueries({ queryKey: ['batch', 'detail'] });
          toast('Batch saved.', { variant: 'success' });
          setDrawer(null);
        }}
      />
    </ListShell>
    </ListOrOutlet>
  );
}
